import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Clock, Trash2, LogOut, Plus, Heart, FileText, User } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SEOHead from "@/components/SEOHead";

export default function PatientDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("citas");

  useEffect(() => {
    if (loading) return;
    if (!user) nav("/paciente/login", { replace: true });
    else if (user.role !== "patient") { toast.error("Esta sesión no es de paciente"); nav("/"); }
  }, [user, loading]);

  if (loading || !user || user.role !== "patient") return <div className="p-10">Cargando…</div>;

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };

  return (
    <>
      <SEOHead title="Mi panel · Katia Falón Odontología" description="Panel del paciente" path="/paciente/panel" />
      <section data-testid="patient-dashboard" className="mx-auto max-w-5xl px-6 py-12 lg:py-16">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">Hola, {user.name}</div>
            <h1 className="mt-1 font-display text-4xl sm:text-5xl">Mi panel</h1>
            <p className="mt-2 text-sm text-[hsl(var(--clinic-muted))]">{user.email}</p>
          </div>
          <button onClick={doLogout} data-testid="patient-logout" className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--clinic-border))] px-4 py-2 text-xs font-semibold text-[hsl(var(--clinic-secondary))]">
            <LogOut size={12} /> Cerrar sesión
          </button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-[hsl(var(--clinic-border))]">
          {[["citas","Mis citas",CalendarDays],["historial","Historial médico",Heart],["perfil","Mi perfil",User]].map(([k,l,Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`patient-tab-${k}`} className={`inline-flex items-center gap-1 border-b-2 px-3 py-3 text-sm font-semibold ${tab === k ? "border-[hsl(var(--clinic-secondary))] text-[hsl(var(--clinic-secondary))]" : "border-transparent text-[hsl(var(--clinic-muted))]"}`}>
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>
        <div className="py-8">
          {tab === "citas" && <BookingsSection />}
          {tab === "historial" && <HistorySection />}
          {tab === "perfil" && <ProfileSection user={user} />}
        </div>
      </section>
    </>
  );
}

function BookingsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(null); // booking id
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const load = () => { setLoading(true); api.get("/patient/bookings").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!newDate) { setAvailableSlots([]); return; }
    api.get(`/slots?day=${newDate}`).then((r) => setAvailableSlots(r.data.slots || []));
  }, [newDate]);
  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta cita?")) return;
    try { await api.delete(`/patient/bookings/${id}`); toast.success("Cita cancelada"); load(); }
    catch { toast.error("No se pudo cancelar"); }
  };
  const reschedule = async (id) => {
    if (!newDate || !newTime) return toast.error("Elige día y hora");
    try {
      await api.post(`/patient/bookings/${id}/reschedule`, { slot_date: newDate, slot_time: newTime });
      toast.success("Cita reprogramada");
      setRescheduling(null); setNewDate(""); setNewTime(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const downloadPdf = async () => {
    const resp = await api.get("/patient/medical-history/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = "historial.pdf"; a.click(); URL.revokeObjectURL(url);
  };
  const now = new Date();
  const upcoming = items.filter((b) => new Date(`${b.slot_date}T${b.slot_time}`) >= now);
  const past = items.filter((b) => new Date(`${b.slot_date}T${b.slot_time}`) < now);
  if (loading) return <p className="text-sm">Cargando…</p>;
  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl">Próximas</h2>
        <div className="flex gap-2">
          <button onClick={downloadPdf} data-testid="patient-download-pdf" className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--clinic-border))] px-4 py-2 text-xs font-semibold">
            Descargar historial PDF
          </button>
          <Link to="/reservar" data-testid="patient-new-booking" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--clinic-secondary))] px-4 py-2 text-xs font-semibold text-white"><Plus size={12} /> Nueva cita</Link>
        </div>
      </div>
      {upcoming.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--clinic-border))] p-8 text-center text-sm text-[hsl(var(--clinic-muted))]">
          No tienes citas próximas. <Link to="/reservar" className="font-semibold text-[hsl(var(--clinic-secondary))]">Reserva una ahora</Link>.
        </div>
      )}
      <div className="grid gap-3">
        {upcoming.map((b) => (
          <div key={b.id} data-testid={`patient-booking-${b.id}`} className="rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-display text-xl">{b.treatment_name}</div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-[hsl(var(--clinic-muted))]">
                  <span className="inline-flex items-center gap-1"><CalendarDays size={14} /> {b.slot_date}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={14} /> {b.slot_time}</span>
                </div>
                {b.notes && <p className="mt-2 text-xs italic text-[hsl(var(--clinic-muted))]">"{b.notes}"</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { setRescheduling(rescheduling === b.id ? null : b.id); setNewDate(""); setNewTime(""); }} data-testid={`patient-reschedule-${b.id}`} className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--clinic-border))] px-4 py-2 text-xs font-semibold">
                  Reprogramar
                </button>
                <button onClick={() => cancel(b.id)} data-testid={`patient-cancel-${b.id}`} className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--clinic-border))] px-4 py-2 text-xs font-semibold text-[hsl(var(--clinic-secondary))]">
                  <Trash2 size={12} /> Cancelar
                </button>
              </div>
            </div>
            {rescheduling === b.id && (
              <div className="mt-4 rounded-xl bg-[hsl(var(--clinic-bg))] p-4">
                <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Nuevo día</span>
                    <input type="date" data-testid={`reschedule-date-${b.id}`} value={newDate} min={new Date().toISOString().split("T")[0]} onChange={(e) => setNewDate(e.target.value)} className="mt-1 w-full rounded-lg border border-[hsl(var(--clinic-border))] px-3 py-2 text-sm" />
                  </label>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Nueva hora</span>
                    {!newDate && <p className="mt-2 text-xs">Elige día primero.</p>}
                    {newDate && availableSlots.length === 0 && <p className="mt-2 text-xs">Sin disponibilidad ese día.</p>}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {availableSlots.map((s) => (
                        <button key={s} type="button" onClick={() => setNewTime(s)} data-testid={`reschedule-slot-${b.id}-${s}`} className={`rounded-full border px-2 py-1 text-xs ${newTime === s ? "border-[hsl(var(--clinic-secondary))] bg-[hsl(var(--clinic-secondary))] text-white" : "border-[hsl(var(--clinic-border))]"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <button onClick={() => reschedule(b.id)} data-testid={`reschedule-confirm-${b.id}`} className="mt-3 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--clinic-secondary))] px-4 py-2 text-xs font-semibold text-white">Confirmar reprogramación</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {past.length > 0 && (
        <div>
          <h2 className="mt-8 font-display text-2xl">Anteriores</h2>
          <div className="mt-4 grid gap-3">
            {past.map((b) => (
              <div key={b.id} className="rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-5 opacity-70">
                <div className="font-display text-lg">{b.treatment_name}</div>
                <div className="mt-1 flex items-center gap-3 text-sm text-[hsl(var(--clinic-muted))]">
                  <span className="inline-flex items-center gap-1"><CalendarDays size={14} /> {b.slot_date}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={14} /> {b.slot_time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HistorySection() {
  const [form, setForm] = useState({ allergies: "", chronic_conditions: "", current_medications: "", previous_treatments: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    api.get("/patient/medical-history").then((r) => {
      const d = r.data || {};
      setForm({
        allergies: d.allergies || "",
        chronic_conditions: d.chronic_conditions || "",
        current_medications: d.current_medications || "",
        previous_treatments: d.previous_treatments || "",
        notes: d.notes || "",
      });
      setUpdatedAt(d.updated_at || null);
    }).finally(() => setLoading(false));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/patient/medical-history", form);
      setUpdatedAt(r.data.updated_at);
      toast.success("Historial actualizado");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };
  if (loading) return <p className="text-sm">Cargando…</p>;
  return (
    <form onSubmit={save} data-testid="patient-history-form" className="rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-6">
      <div className="mb-6 rounded-xl bg-[hsl(var(--clinic-bg))] p-4 text-sm">
        <p className="font-semibold">Historial autoreportado</p>
        <p className="mt-1 text-xs text-[hsl(var(--clinic-muted))]">Esta información es orientativa y complementa —nunca sustituye— la valoración clínica. La Dra. Katia Falón la revisará y validará en tu próxima consulta.</p>
        {updatedAt && <p className="mt-2 text-xs text-[hsl(var(--clinic-muted))]">Última actualización: {new Date(updatedAt).toLocaleString("es-CO")}</p>}
      </div>
      <div className="grid gap-4">
        <TextArea label="Alergias conocidas" placeholder="Penicilina, latex, ibuprofeno…" value={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} testId="mh-allergies" />
        <TextArea label="Condiciones crónicas" placeholder="Diabetes tipo 2, hipertensión…" value={form.chronic_conditions} onChange={(v) => setForm({ ...form, chronic_conditions: v })} testId="mh-chronic" />
        <TextArea label="Medicación actual" placeholder="Losartán 50mg cada 24h…" value={form.current_medications} onChange={(v) => setForm({ ...form, current_medications: v })} testId="mh-medications" />
        <TextArea label="Tratamientos odontológicos previos" placeholder="Endodoncias, ortodoncia 2020…" value={form.previous_treatments} onChange={(v) => setForm({ ...form, previous_treatments: v })} testId="mh-previous" />
        <TextArea label="Notas adicionales" placeholder="Miedo dental, embarazo, contexto…" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="mh-notes" />
      </div>
      <button type="submit" disabled={saving} data-testid="mh-save" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
        <FileText size={14} /> {saving ? "Guardando…" : "Guardar historial"}
      </button>
    </form>
  );
}

function TextArea({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">{label}</span>
      <textarea rows={2} placeholder={placeholder} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full resize-none rounded-xl border border-[hsl(var(--clinic-border))] bg-white px-4 py-3 text-sm" />
    </label>
  );
}

function ProfileSection({ user }) {
  return (
    <div className="grid gap-4 rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-6">
      <Row label="Nombre" value={user.name} />
      <Row label="Correo" value={user.email} />
      <Row label="Teléfono" value={user.phone || "—"} />
      <Row label="Fecha de nacimiento" value={user.dob || "—"} />
      <p className="text-xs italic text-[hsl(var(--clinic-muted))]">Para modificar estos datos escríbenos por WhatsApp a la clínica.</p>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-[hsl(var(--clinic-border))] pb-2 last:border-0 last:pb-0">
      <span className="text-xs uppercase tracking-widest text-[hsl(var(--clinic-muted))]">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
